import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Axis, Color3 } from '@babylonjs/core/Maths/math';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { KbVector } from '@kb3d/models/classes';
import { instanceOf } from '@kb3d/models/classes/decorators';
import { eConnectorPositionMode, eSketchPathType } from '@kb3d/models/classes/enums';
import { BaseConnector } from '@kb3d/models/classes/features/base-connector';
import { SketchControlPoint } from '@kb3d/models/classes/features/sketch-control-point';
import { Kb3dObject } from '@kb3d/models/classes/kb3d-object';
import { SketchPath } from '@kb3d/models/classes/meshes/sketch-node-path';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { IBoundingBox } from '@kb3d/models/classes/primitives/primitive-utilities';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass, isTrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { BoundingBoxRelativeHelper } from '@kb3d/view/helpers/bounding-box-relative-helper';
import { getRotationBtwVectors, toRadians } from '@kb3d/view/helpers/math-helper';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { RendererManager } from '../renderer-manager';
import { CONNECTOR_DIAM, ConnectorParts, ConnectorRenderer, IConnectorLayer } from './connector';

interface IConnectorLayerWithParts extends IConnectorLayer {
    parts: Map<string, ConnectorParts>;
}

const getMeshName = {
    handles: (dynamicId: string) => `${dynamicId}_handles`,
    handleBar: (dynamicId: string) => `${dynamicId}_handleBar`,
    directionHandle1: (dynamicId: string) => `${dynamicId}_directionHandle1`,
    directionHandle2: (dynamicId: string) => `${dynamicId}_directionHandle2`,
};

export class SketchControlPointRenderer extends ConnectorRenderer<SketchControlPoint> {
    protected layers: Map<'real' | 'utility', IConnectorLayerWithParts>;
    protected readonly controlPointScaleRatio = 0.12;

    constructor(viewer: KbViewer, scene: Scene, rendererManager: RendererManager) {
        super(viewer, scene, rendererManager);
        this.layers.forEach(layer => {
            layer.parts = new Map();
        });
    }
    modelToken = Token.SketchControlPoint;

    public handlesNode(node: Kb3dObject): boolean {
        return instanceOf(node, Token.SketchControlPoint);
    }

    update3dInternal(c: ITrackedClass<SketchControlPoint>) {
        const realLayer = this.layers.get('real')!;
        const utilityLayer = this.layers.get('utility')!;
        const { mesh, utilityMesh } = super.update3dInternal(c as ITrackedClass<SketchControlPoint>, true);

        if (c._parent instanceof SketchPath) {
            const realParts = realLayer.parts.get(c._dynamicId);
            const utilityParts = utilityLayer.parts.get(c._dynamicId);

            if (c.changes.hasAny('direction', 'direction2')) {
                if (realParts) {
                    updateHandles(realParts, c, c._parent);
                }
                if (utilityParts) {
                    updateHandles(utilityParts, c, c._parent);
                }
            }
        }

        return { mesh, utilityMesh };

        function updateHandles(parts: ConnectorParts, controlPoint: SketchControlPoint, path: SketchPath) {
            let enableHandle1: boolean, enableHandle2: boolean;

            switch (path.type) {
                case eSketchPathType.arcThrough3Points:
                case eSketchPathType.points:
                case eSketchPathType.centerRadiusCircle:
                case eSketchPathType.catmullRom:
                    enableHandle1 = false;
                    enableHandle2 = false;
                    break;

                case eSketchPathType.quadraticBezier:
                case eSketchPathType.hermite:
                    // Don't show handles on the ends of the path since that handle does nothing
                    enableHandle1 =
                        path.controlPoints.findIndex(cp => cp._dynamicId === controlPoint._dynamicId) <
                        path.controlPoints.length - 1;
                    enableHandle2 = false;
                    break;

                default:
                    // Don't show handles on the ends of the path since that handle does nothing
                    enableHandle1 =
                        path.controlPoints.findIndex(cp => cp._dynamicId === controlPoint._dynamicId) <
                        path.controlPoints.length - 1;
                    enableHandle2 = path.controlPoints.findIndex(cp => cp._dynamicId === controlPoint._dynamicId) > 0;
            }

            if (parts.directionHandle1 && c.direction.length() > 0) {
                parts.directionHandle1.position = c.direction.toVec3();
            }
            if (parts.directionHandle2 && c.direction2 && c.direction2.length() > 0) {
                parts.directionHandle2.position = c.direction2.toVec3();
            }
            parts.directionHandle1?.setEnabled(enableHandle1);
            parts.directionHandle2?.setEnabled(enableHandle2);

            if (parts.handleBar) {
                parts.handleBar.setEnabled(true);
                parts.handleBar = MeshBuilder.CreateDashedLines(getMeshName.handleBar(c._dynamicId), {
                    points: [
                        enableHandle1 ? c.direction.toVec3() : Vector3.Zero(),
                        Vector3.Zero(),
                        enableHandle2 && c.direction2 ? c.direction2.toVec3() : Vector3.Zero(),
                    ],
                    updatable: true,
                    instance: parts.handleBar,
                });
            }
        }
    }

    delete3d(c: ITrackedClass<SketchControlPoint>) {
        const mesh = this.getMesh(c, this.layers.get('real')!);
        const utilityMesh = this.getMesh(c, this.layers.get('utility')!);
        this.viewer.camera.clearMaintainedNodeScreenSize(mesh);
        if (utilityMesh) {
            this.viewer.camera.clearMaintainedNodeScreenSize(utilityMesh);
        }
        super.delete3d(c);

        const sketchPath = c._parent;
        if (sketchPath && isTrackedClass(sketchPath)) {
            sketchPath.setPropertyIsModified('controlPoints', true);
        }
    }

    updateDirection(c: SketchControlPoint) {
        const realLayer = this.layers.get('real')!;
        const utilityLayer = this.layers.get('utility')!;
        const mesh = this.getMesh(c, realLayer);
        const utilityMesh = this.getMesh(c, utilityLayer);

        const deltaBtwXaxisAndDirection = getRotationBtwVectors(
            Axis.X,
            this.getRelativeDirection(c, c.direction).normalize()
        );
        const angleRot = Quaternion.RotationAxis(Axis.X, toRadians(c.angle));
        mesh.rotationQuaternion = utilityMesh.rotationQuaternion = deltaBtwXaxisAndDirection.multiply(angleRot);

        const realParts = realLayer.parts.get(c._dynamicId);
        const utilityParts = utilityLayer.parts.get(c._dynamicId);
        if (realParts?.handleContainer) {
            const invTransform = Matrix.Compose(Vector3.One(), mesh.rotationQuaternion, Vector3.Zero());
            invTransform.invert();
            invTransform.decomposeToTransformNode(realParts.handleContainer);
            if (utilityParts?.handleContainer) {
                invTransform.decomposeToTransformNode(utilityParts.handleContainer);
            }
        }

        this.updateScale(c);
    }

    updatePosition(c: SketchControlPoint, bb?: IBoundingBox) {
        const mesh = this.getMesh(c, this.layers.get('real')!);
        const utilityMesh = this.getMesh(c, this.layers.get('utility')!);

        mesh.position = this.getControlPointAbsPosition(c, bb);
        if (utilityMesh) {
            utilityMesh.position = mesh.position;
        }

        const sketchPath = c._parent;
        if (sketchPath && isTrackedClass(sketchPath)) {
            sketchPath.setPropertyIsModified('controlPoints', true);
        }
    }

    getControlPointAbsPosition(c: SketchControlPoint, bb?: IBoundingBox) {
        if (c.positionMode == eConnectorPositionMode.boundingBox) {
            if (!bb) {
                bb = c.getSketch()?.getBoundingBox();
            }
            if (bb) {
                return BoundingBoxRelativeHelper.convertRelativeToAbsolute(c, bb);
            } else {
                return new Vector3(c.position.x, c.position.y, c.position.z);
            }
        } else {
            return c.position.toVec3();
        }
    }

    getRelativeDirection(c: SketchControlPoint, direction: KbVector, bb?: IBoundingBox) {
        if (c.positionMode === eConnectorPositionMode.absolute) {
            return direction.toVec3();
        } else {
            if (!bb) {
                bb = c.getSketch()?.getBoundingBox();
            }
            if (!bb) {
                return Vector3.Zero();
            }
            return BoundingBoxRelativeHelper.convertRelativeDirectionToAbsolute(c, direction, bb);
        }
    }

    protected getMesh(c: BaseConnector, layer: IConnectorLayerWithParts) {
        let mesh = layer.map.get(c._dynamicId);
        if (!mesh) {
            if (!layer.template) {
                layer.template = this.createMeshTemplate(layer);
            }

            mesh = layer.template.clone(c._dynamicId); // layer.template.createInstance(c._dynamicId);
            mesh.id = c._dynamicId;

            const [handleContainer] = mesh.getChildren() as TransformNode[];
            const [directionHandle1, directionHandle2, _handleBar] = handleContainer.getChildMeshes(true);
            const handleBar = _handleBar as LinesMesh;
            handleBar.makeGeometryUnique();

            handleContainer.id = getMeshName.handles(c._dynamicId);
            directionHandle1.id = getMeshName.directionHandle1(c._dynamicId);
            directionHandle2.id = getMeshName.directionHandle2(c._dynamicId);

            const parentNode = c._parent?._parent;

            if (parentNode && parentNode instanceof SpaceNode) {
                const parent3d = this.viewer.getRendererNode(parentNode);
                if (parent3d) {
                    mesh.setParent(parent3d);
                }
            }

            mesh.isPickable = layer.pickable;
            mesh.position = Vector3.Zero();
            mesh.rotationQuaternion = Quaternion.Identity();
            layer.map.set(c._dynamicId, mesh);
            layer.parts.set(c._dynamicId, {
                mesh,
                handleContainer: handleContainer as TransformNode,
                directionHandle1,
                directionHandle2,
                handleBar: handleBar,
            });
        }
        return mesh;
    }

    protected createMeshTemplate(layer: IConnectorLayer) {
        const mesh = super.createMeshTemplate(layer);

        const handleContainer = new TransformNode('', this.scene);

        const handleBall = CreateSphere('handle1', {
            segments: 4,
            diameter: CONNECTOR_DIAM / 2,
        });
        handleBall.setParent(handleContainer);
        const m = new StandardMaterial('controlPointMaterial', this.scene);
        m.emissiveColor = this.viewer.highlightColor.toColor3();
        m.disableLighting = true;
        handleBall.material = m;

        const handle1 = handleBall;
        const handle2 = handleBall.clone('handle2', handleContainer, true);
        handle1.setEnabled(false);
        handle2.setEnabled(false);
        const baseColor = this.viewer.sceneNode?.edgeColor
            ? this.viewer.sceneNode.edgeColor.toColor3()
            : Color3.White();
        const handleBar = MeshBuilder.CreateDashedLines('handleBar', {
            points: [Vector3.Up(), Vector3.Zero(), Vector3.Down()],
            updatable: true,
            dashSize: 1,
            gapSize: 1,
            dashNb: 30,
        });
        handleBar.color = baseColor;
        handleBar.isPickable = false;
        // const handleBarMaterial = new StandardMaterial('controlPointHandleBarMaterial', this.scene);
        // handleBarMaterial.emissiveColor = this.viewer.sceneNode?.edgeColor ? this.viewer.sceneNode.edgeColor.toColor3() : Color3.White();
        // handleBar.material = handleBarMaterial;
        handleBar.setParent(handleContainer);
        handleContainer.setParent(mesh);

        return mesh;
    }

    protected addSubscription(c: ITrackedClass<SketchControlPoint>) {
        const sketch = c.getSketch();

        if (sketch && !this.subscriptionMap.has(c)) {
            this.subscriptionMap.set(
                c,
                sketch._boundingBoxChanges.subscribe(bbInfo => {
                    this.updatePosition(c, bbInfo);
                    this.updateScale(c);
                })
            );
        }
    }
}
